import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Loader from 'eventtia-ui-components/lib/Loader';
import SubpageTitle from '../../components/SubpageTitle';
import TabletExitButton from '../../components/TabletExitButton';
import PersonCard from '../../components/PersonCard';
import { getCurrentPerson } from '../../helpers/getters';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import callApi from '../../actions/callApi';
import { addChannel } from '../../actions/messages';
import { setActiveChannel } from '../../actions/app';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 785,
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 4),
    },
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 15,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(6),
  },
  cardsWrapper: {
    display: 'flex',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(5),
    },
  },
  cardContainer: {
    width: '45%',
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },
}));

const ATTENDEE_IDS = ['48425096', '49772962'];

const NeedHelp = ({
  attendeesList,
  isFetchingSingleAttendee,
  attendeeTypes,
  attendeeTypeCustomFields,
  callApi: dispatchCallApi,
}) => {
  const classes = useStyles();

  useEffect(() => {
    ATTENDEE_IDS.forEach((id) => {
      dispatchCallApi('singleAttendee', { id });
    });
  }, [dispatchCallApi]);

  const parsedAttendees = useMemo(() => (
    attendeesList.map((attendee) => getCurrentPerson(
      attendee, {}, attendeeTypes, attendeeTypeCustomFields
    ))

  ), [attendeesList, attendeeTypes, attendeeTypeCustomFields]);

  return (
    <div className={classes.root}>
      <Loader
        variant="absolute"
        loading={isFetchingSingleAttendee}
      />

      <TabletExitButton />

      <SubpageTitle text="Need help?" />

      <Typography className={classes.sectionTitle} variant="subtitle2">
        Logistical Support
      </Typography>

      {!!parsedAttendees.length && (
        <div className={classes.cardsWrapper}>
          {parsedAttendees.map((person) => (
            <div key={person.id} className={classes.cardContainer}>
              <PersonCard
                person={person}
                showContactInfo
                showPhone
                fullWidth
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

NeedHelp.propTypes = {
  attendeesList: PropTypes.arrayOf(
    CustomPropTypes.attendee
  ),
  isFetchingSingleAttendee: PropTypes.bool.isRequired,
  attendeeTypes: PropTypes.objectOf(
    CustomPropTypes.attendeeType
  ).isRequired,
  attendeeTypeCustomFields: PropTypes.objectOf(
    CustomPropTypes.attendeeTypeCustomField
  ).isRequired,
  callApi: PropTypes.func.isRequired,
};

NeedHelp.defaultProps = {
  attendeesList: [],
};

const mapStateToProps = ({
  entities: {
    attendees,
    attendeeTypes,
    attendeeTypeCustomFields,
  },
  fetchStatus: {
    singleAttendee: {
      isFetching: isFetchingSingleAttendee,
    },
  },
}) => {
  const attendeesList = Object.values(attendees || {}).filter(
    (attendee) => ATTENDEE_IDS.includes(attendee.id)
  );

  return {
    attendeesList,
    isFetchingSingleAttendee,
    attendeeTypes,
    attendeeTypeCustomFields,
  };
};

export default connect(mapStateToProps, {
  callApi,
  addChannel,
  setActiveChannel,
})(NeedHelp);
